import React, {createContext, useCallback, useContext, useMemo, useRef, useState} from "react";
import {Overlay, Tooltip} from "react-bootstrap";
import {TOOLTIP_POPPER_CONFIG} from "./tooltipConfig.js";

const ZellTooltipContext = createContext(null);

/**
 * Ein einziger Tooltip-Baustein für die ganze Lieferfähigkeits-Tabelle
 * (528 Zeilen × 16+ Wochenspalten, Auftrag AP3 B "Performance"): statt
 * pro Zelle ein eigenes OverlayTrigger/Popper-Paar vorzuhalten, meldet
 * jede Zelle beim Hover/Fokus nur ihren DOM-Knoten und ihren Text an
 * diesen gemeinsamen Kontext - gerendert wird EIN <Overlay>, das seine
 * Zielposition wechselt. So bleiben Tausende Zellen bloße <span>s mit
 * zwei Event-Handlern statt eigener Overlay-Instanzen.
 */
export function ZellTooltipProvider({children}) {
    const [ziel, setZiel] = useState(null);
    const [inhalt, setInhalt] = useState(null);
    const verstecken = useRef(null);

    const zeigen = useCallback((knoten, text) => {
        if (!text) {
            return;
        }
        clearTimeout(verstecken.current);
        setZiel(knoten);
        setInhalt(text);
    }, []);

    const ausblenden = useCallback(() => {
        verstecken.current = window.setTimeout(() => setZiel(null), 40);
    }, []);

    const value = useMemo(() => ({zeigen, ausblenden}), [zeigen, ausblenden]);

    return (
        <ZellTooltipContext.Provider value={value}>
            {children}
            <Overlay show={Boolean(ziel && inhalt)} target={ziel} placement="top" flip popperConfig={TOOLTIP_POPPER_CONFIG} rootClose={false}>
                {(overlayProps) => (
                    <Tooltip id="cosmos-zell-tooltip" {...overlayProps}>
                        {inhalt}
                    </Tooltip>
                )}
            </Overlay>
        </ZellTooltipContext.Provider>
    );
}

/**
 * Bindet ein Element an den gemeinsamen Tooltip - liefert ref + die vier
 * Event-Handler, die auf ein beliebiges Element gespreizt werden. Ohne
 * Text (z. B. leere Zelle) werden gar keine Handler zurückgegeben.
 */
export function useZellTooltip(text) {
    const ctx = useContext(ZellTooltipContext);
    const ref = useRef(null);

    if (!ctx || !text) {
        return {ref, tooltipProps: {}};
    }

    return {
        ref,
        tooltipProps: {
            tabIndex: 0,
            onMouseEnter: () => ctx.zeigen(ref.current, text),
            onMouseLeave: ctx.ausblenden,
            onFocus: () => ctx.zeigen(ref.current, text),
            onBlur: ctx.ausblenden,
        },
    };
}

/** Fertige Zelle mit angebundenem Tooltip - Kurzform für die Tabelle. */
export function ZellMitTooltip({text, className = "", style, children}) {
    const {ref, tooltipProps} = useZellTooltip(text);

    return (
        <span ref={ref} className={className} style={style} {...tooltipProps}>
            {children}
        </span>
    );
}
